/**
 * @fileoverview Zoom controls is a form of encapsulation for the zooming
 * functionality. It is used by the zoom buttons on the toolbar.
 */
import { getViewport, isViewportInitialized } from "./viewport.layer";
import type { FederatedWheelEvent } from "pixi.js";
import {
  VIEWPORT_INITIAL_OFFSET_X,
  VIEWPORT_INITIAL_OFFSET_Y,
  ZOOM_ANIMATION_DURATION,
  ZOOM_FACTOR,
  ZOOM_MAX_SCALE,
  ZOOM_MIN_SCALE
} from "./viewport.constants";

const zoomIn = (): void => {
  if (!isViewportInitialized()) return; // viewport not initialized
  const viewport = getViewport();
  const currentScale = viewport.scale.x;
  let targetScale = viewport.scale.x + ZOOM_FACTOR;

  if (targetScale > ZOOM_MAX_SCALE) {
    targetScale = ZOOM_MAX_SCALE;
  }

  if (targetScale === currentScale) {
    return;
  }

  viewport.animate({
    scaleX: viewport.scale.x + ZOOM_FACTOR,
    scaleY: viewport.scale.y + ZOOM_FACTOR,
    time: ZOOM_ANIMATION_DURATION
  });
};

const zoomOut = (): void => {
  if (!isViewportInitialized()) return; // viewport not initialized
  const viewport = getViewport();
  const currentScale = viewport.scale.x;
  let targetScale = viewport.scale.x - ZOOM_FACTOR;

  if (targetScale < ZOOM_MIN_SCALE) {
    targetScale = ZOOM_MAX_SCALE;
  }

  if (targetScale === currentScale) {
    return;
  }

  viewport.animate({
    scaleX: viewport.scale.x - ZOOM_FACTOR,
    scaleY: viewport.scale.y - ZOOM_FACTOR,
    time: ZOOM_ANIMATION_DURATION
  });
};

const resetZoomAndRecenter = (): void => {
  if (!isViewportInitialized()) return; // viewport not initialized
  const viewport = getViewport();
  viewport.animate({
    scaleX: 1,
    scaleY: 1,
    time: ZOOM_ANIMATION_DURATION,
    position: {
      x: viewport.screenWidth / 2 - VIEWPORT_INITIAL_OFFSET_X,
      y: viewport.screenHeight / 2 - VIEWPORT_INITIAL_OFFSET_Y
    }
  });
};

/**
 * Use this to set initial zoom after viewport is initialized during onload
 */
const setMaxZoomOut = (): void => {
  if (!isViewportInitialized()) return; // viewport not initialized
  getViewport().setZoom(ZOOM_FACTOR);
};

export const pauseWheelPlugin = () => {
  getViewport().plugins.pause("wheel");
};
export const resumeWheelPlugin = () => {
  getViewport().plugins.resume("wheel");
};

export const zoomControl = {
  zoomIn,
  zoomOut,
  resetZoomAndRecenter,
  setMaxZoomOut
};

/**
 * This is required to support zooming with wheel when meta key is pressed.
 * When wheel plugin is active with wheelZoom as true, then,
 */
export const handleWheelPluginOnMetaCtrlKey = (e: FederatedWheelEvent) => {
  if (e.metaKey || e.ctrlKey) {
    // Resume the Wheel Plugin to perform zooming, and override the scrolling actions by drag plugin
    resumeWheelPlugin();
  } else {
    // Pause the wheel plugin, so drag plugin can take over the scrolling actions
    pauseWheelPlugin();
  }
};
